import React, { Component, ReactNode, useState } from "react";
import { findDOMNode } from "react-dom";
import AntdTable, {
  ColumnProps,
  TableComponents,
  TableProps
} from "antd/lib/table";
import cloneDeep from "lodash/cloneDeep";
import PropTypes from "prop-types";
import { Resizable } from "react-resizable";
import isEqual from "lodash/isEqual";
import isFunction from "lodash/isFunction";
import lodashGet from "lodash/get";
import find from "lodash/find";
import { DragableBodyRow } from "./dragTable";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { EditableFormCell, EditableCell } from "./editCell";
import { EditableFormRow, EditableRow } from "./editRow";
import update from "immutability-helper";
import PagePointEnhanced from "../page-point/pagePointEnhanced";
import PagePoint from "../page-point/index";
import { ConfigConsumer, ConfigConsumerEProps } from "../config-provider";
import message from "../message";
import Icon from "../icon";
import Dropdown from "../dropdown";
import Column from "antd/lib/table/Column";
import "antd/lib/table/style";
import "@whalecloud/fdx-theme-fdx/table.less";
import ColModelMenu from './ColModelMenu';

export interface IColumnProps extends ColumnProps<object> {
  editable?: boolean;
  editType?: string;
  editRules?: any;
  editOptions?: any;
  optionsLabel?: string;
  optionsValue?:string;
  editInitValue?: any;
  relationKey?: string;
  editContent?: () => string;
  saveContent?: () => string;
  cancelContent?: () => string;
}
interface FTableProps extends TableProps<object> {
  editingKey?: string;
  highlightRow?: object;
  rowKey?: string | ((record: object, index?: number) => string);
  onSaveCellEdit?: (record: object, index: number) => void;
  onSaveRowEdit?: (record: object, data: object[]) => void;
  onCancelRowEdit?: (record: object) => void;
  onRowEditing?: (record: object) => void;
  onColumnResize?: (record: object) => void;
  afterRowDrag?: (record: object) => void;
  onSortChange?: (sortField: string, sortOrder: string) => void;
  onSelectRows?: (selectedKeys: string[]) => void;
  dragable?: boolean;
  editRow?: boolean;
  selectType?: string;
  saveAddRowMsg?: string;
  colModel?: boolean; // 是否开启 显示/隐藏列 按钮
  autoHideSortIcon?: boolean;
  autoHideFilterIcon?: boolean;
  onColModelChange?: (record: object) => void;
}

interface SortProp {
  field?: string,
  order?: string;
}
interface FTableState {
  columns: IColumnProps[];
  dataSource: object[];
  copyDataSource: object[];
  editingKey: string;
  update: boolean;
  highlightRow: object;
  tableWidth: number;
  currentSortInfo: SortProp;
  currentPage: number;
  freezAddRow: boolean;
  defaultColumns: IColumnProps[];
}

interface IChangeObj {
  editingKey?: string;
  update?: boolean;
  columns?: ColumnProps<object>[];
  highlightRow?: object;
  dataSource?: object[];
  copyDataSource?: object[];
  currentPage?: number;
  freezAddRow?: boolean;
  defaultColumns?: IColumnProps[];
}
const DEFAULT_COLUMN_WIDTH = 100;
const DEFAULT_COLUMN_EDITTYPE = "text";
const DEFAULT_COLUMN_OPTIONS_LABEL = "name";
const DEFAULT_COLUMN_OPTIONS_VALUE = "value";
const DEFAULT_PAGE_SIZE = 10;

const {
  sendPagePoint,
  pagePointConfig: { regKey },
  EventType: { clickType }
} = PagePoint;

// #160 处理表格列头拖拽效果
let resizeHandle = null;
let scrollTableClass = '';
let scrollTableCss = '';
const SCROLL_TABLE_CLASS = '.ant-table-header.ant-table-hide-scrollbar';
const FDX_SCROLL_TABLE_CLASS = 'fdx-marked-scroll-table';
const TABLE_HEIGHT_CSS = '--handler-height';
const RESIZ_HANDLER_ACTIVE_CLASS = 'react-resizable-handle-active';
const TABLE_SCROLL_BODY = '.ant-table-scroll';
const FDX_TABLE_SCROLL_BODY = 'fdx-table-scroll'
const FDX_ON_RESIZE_BODY = 'fdx-on-resize' // 拖拽时取消文本选中样式

// 动态为columnItem创建dataIndex(lowcode专用)
const handleColumns = (columns) => {
  if (!columns) return columns;
  columns.forEach(column => {
    if (!column.dataIndex && column.render) {
      const render = column.render.toString();
      // 处理column没有dataIndex属性情况
      if (render.includes('createElement("span"')) {
        const matches = render.match(/createElement\(\"span\"[\s\S]*?\)/gi);
        let dataIndex = '';
        if (matches.length > 0) {
          const dataIndexExpresses = matches[0].split(',');
          if (dataIndexExpresses.length) {
            const dataIndexExpress = dataIndexExpresses[dataIndexExpresses.length - 1];
            const[, express] = dataIndexExpress.split('.');
            if (express) {
              dataIndex = express.replace(')', '');
            }
          }
        }
        if (dataIndex) {
          column.dataIndex = dataIndex;
        }
      }
    }
  });
  return columns;
}

const caluColumnWidth = (columns, tableWidth) => {
  let someWidth = 0;
  let noWidthCnt = 0;
  let newColumns = columns.map(col => {
    let width;
    if (typeof col.width === "string") {
      if (col.width.includes("%")) {
        width = Math.floor(
          tableWidth *
            (parseInt(col.width.replace("%", "")) / 100)
        );
      } else {
        width = +col.width;
      }
    } else if (typeof col.width === "number"){
      width = col.width;
    } else {
      width = 0;
      noWidthCnt ++;
    }
    someWidth += width;
    return {
      ...col,
      width
    };
  });
  if (noWidthCnt) {
    let restWidth = parseFloat(((tableWidth - someWidth) / noWidthCnt).toFixed(2));
    newColumns = newColumns.map((col, index) => {
      if (!col.width) {
        if (index !== newColumns.length - 1) {
          col.width = restWidth;
        } else {
          delete col.width
        }
      }
      return col;
    });
  }
  return newColumns;
}

// 根据value找到key(深度遍历)
const findKeyByValue = (value: any, obj: any) => {
  const allKeys = {};
  const map = (mapObj: any, parentKey?: string) => {
    if (typeof mapObj === 'object' && !Array.isArray(mapObj)) {
      Object.keys(mapObj).forEach(key => {
        let realKey = key;
        const val = mapObj[key];
        if (parentKey) {
          realKey = `${parentKey}.${key}`;
        }
        allKeys[realKey] = val;
        if (val && typeof val === 'object') {
          map(val, realKey)
        }
      })
    }
  }
  map(obj);
  const targetKey = Object.keys(allKeys).find(key => allKeys[key] === value);
  return targetKey; 
}

class Table extends Component<FTableProps, FTableState> {
  static componentName = "Table";
  static Column = Column;
  static propTypes = {
    dragable: PropTypes.bool,
    editRow: PropTypes.bool,
    onSaveCellEdit: PropTypes.func,
    onSaveRowEdit: PropTypes.func,
    editingKey: PropTypes.string,
    highlightRow: PropTypes.object,
    onCancelRowEdit: PropTypes.func,
    onRowEditing: PropTypes.func,
    onColumnResize: PropTypes.func,
    afterRowDrag: PropTypes.func,
    onSortChange: PropTypes.func,
    onSelectRows: PropTypes.func,
    onColModelChange: PropTypes.func,
  };

  static defaultProps = {
    pagination: false,
    dragable: false,
    bordered: true,
    editRow: false,
    rowKey: "key",
    size: "small",
    //扩展属性
    update: false,
    highlightRow: {}, //指定一行显示选中高亮效果
    editingKey: "", //指定某一行进入编辑状态的行标识
    onSaveCellEdit: () => {}, // 单元格编辑blur/select触发的方法
    onSaveRowEdit: () => {}, //行编辑点击保存按钮触发的方法
    onCancelRowEdit: () => {}, //行编辑点击取消按钮触发的方法
    onRowEditing: () => {}, //点击行编辑时触发的方法
    onColumnResize: () => {}, // 列拖拽时触发的方法
    afterRowDrag: () => {},
    onColModelChange: () => {},// colModel开启时点击checkbox触发的方法
  };

  components: TableComponents;
  table: any;

  constructor(props) {
    super(props);
    const { dataSource, editingKey, highlightRow, children } = this.props;
    let newColumns = [];
    if (children) {
      if (Array.isArray(children)) {
        for (let i = 0; i < (children as Array<any>).length; i++) {
          const child = (children as Array<any>)[i];
          if (child.props) {
            newColumns.push(child.props);
          }
        }
      } else {
        newColumns.push((children as any).props);
      }
      newColumns = handleColumns(cloneDeep(newColumns));
    } else {
      newColumns = handleColumns(cloneDeep(this.props.columns))
    }
    this.state = {
      tableWidth: 0,
      columns: newColumns,
      dataSource,
      editingKey,
      update: false, //更新表格内部状态都需要把update置为true, 切记切记！
      highlightRow,
      currentSortInfo: {},
      currentPage: (this.props.pagination as any)?.current || 1,
      freezAddRow: false,
      copyDataSource: cloneDeep(dataSource),
      defaultColumns: newColumns, // 用来记录外部传进来的原始 columns，用于渲染显示/隐藏列的菜单
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let changeObj: IChangeObj = {};
    if (nextProps.editingKey !== prevState.editingKey) {
      if (nextProps.update === prevState.update) {
        changeObj.editingKey = nextProps.editingKey;
      } else {
        changeObj.update = false;
      }
    }
    if (!isEqual(nextProps.columns, prevState.columns)) {
      if (nextProps.columns && nextProps.update === prevState.update) {
        let newColumns = nextProps.columns;
        if (nextProps.columns.findIndex(col => !col.width) !== -1) {
          //如果传入的columns中有位置width的列，则重新算一下每列的width，确保每一列都有确定的width
          newColumns = caluColumnWidth(nextProps.columns, prevState.tableWidth);
        }
        // 处理显示/隐藏列
        if (nextProps.colModel) {
          if (nextProps.columns.length === prevState.defaultColumns.length) {
            changeObj.columns = newColumns.filter(col => find(prevState.columns, { dataIndex: col?.dataIndex }) || find(prevState.columns, { key: col?.key }));
          } else {
            changeObj.columns = newColumns;
          }
          // table eidt 组件的 columns 是异步生成的，所以要二次获取一下最新的 columns 赋值给 defaultColumns
          if (prevState.columns?.length === 0) changeObj.defaultColumns = newColumns;
        } else {
          changeObj.columns = newColumns;
        }
      } else {
        changeObj.update = false;
      }
      if (changeObj.columns && changeObj.defaultColumns && Array.isArray(changeObj.columns)) {
        changeObj.columns = changeObj.columns.filter(v => !v.defaultHidden);
      }
    }
    if (nextProps.children) {
      const { columns } = prevState;
      const { children } = nextProps;
      let newColumns = [];
      if (Array.isArray(children)) {
        for (let i = 0; i < (children as Array<any>).length; i++) {
          const child = (children as Array<any>)[i];
          if (child.props) {
            newColumns.push(child.props);
          }
        }
      } else {
        newColumns.push(children.props);
      }
      newColumns = handleColumns(cloneDeep(newColumns));
      if (!isEqual(columns, newColumns) && nextProps.update === prevState.update) {
        // 处理显示/隐藏列
        if (nextProps.colModel) {
          if (newColumns.length === prevState.defaultColumns.length) {
            changeObj.columns = newColumns.filter(col => find(columns, { dataIndex: col?.dataIndex }) || find(columns, { key: col?.key }));
          } else {
            changeObj.columns = newColumns;
          }
          // table eidt 组件的 columns 是异步生成的，所以要二次获取一下最新的 columns 赋值给 defaultColumns
          if (columns?.length === 0) changeObj.defaultColumns = newColumns;
        } else {
          changeObj.columns = newColumns;
        }
      } else {
        changeObj.update = false;
      }
    }
    if (!isEqual(nextProps.highlightRow, prevState.highlightRow)) {
      if (nextProps.update === prevState.update) {
        changeObj.highlightRow = nextProps.highlightRow;
      } else {
        changeObj.update = false;
      }
    }
    if (!isEqual(nextProps.dataSource, prevState.dataSource)) {
      if (!prevState.freezAddRow && nextProps.update === prevState.update) {
        changeObj.dataSource = cloneDeep(nextProps.dataSource);
      } else {
        changeObj.copyDataSource = cloneDeep(nextProps.dataSource);
        changeObj.update = false;
      }
      // #164 修复1条后台数据更新组件时导致无法新增编辑行
      if (nextProps.editingKey && nextProps.editRow && (nextProps.dataSource.length - prevState.dataSource.length) === 1) {
        changeObj.freezAddRow = true;
      }

      if (nextProps.dataSource && prevState.dataSource && nextProps.dataSource.length >= prevState.dataSource.length) {
        if (
          !prevState.freezAddRow &&
          nextProps.pagination &&
          !nextProps.pagination.onChange
        ) {
          const pageSize = (nextProps.pagination as any).pageSize || DEFAULT_PAGE_SIZE;
          if (nextProps.dataSource.length / pageSize > prevState.currentPage) {
            changeObj.currentPage = Math.ceil(nextProps.dataSource.length / pageSize);
          }
        }
        if (nextProps.editRow && prevState.freezAddRow && prevState.editingKey && nextProps.editingKey !== prevState.editingKey) {
          changeObj.editingKey = prevState.editingKey;
          if (!isEqual(nextProps.dataSource, prevState.copyDataSource)) {
            message.warn(nextProps.saveAddRowMsg || '请先保存当前新增行！');
            changeObj.dataSource = cloneDeep(prevState.dataSource);
            changeObj.copyDataSource = cloneDeep(prevState.dataSource);
            nextProps.dataSource.pop();
          }
        }
      } else if (nextProps.editRow) {
        changeObj.freezAddRow = false;
        changeObj.editingKey = '';
        changeObj.dataSource = cloneDeep(nextProps.dataSource);
      }
    } else {
      // 如果不把 state 中的 update 重置为 false 在先编辑行，再新增行的场景 新增行会有一行的延迟
      changeObj.update = false;
    }
    return Object.keys(changeObj).length ? changeObj : null;
  }

  componentDidMount() {
    const { columns } = this.state;
    let newColumns = caluColumnWidth(columns, this.table.offsetWidth);
    this.setState({tableWidth: this.table.offsetWidth});
    if (this.table && !isEqual(columns, newColumns)) {
      this.setState({
        columns: newColumns,
        update: true,
      });
    }
  }

  ResizeableTitle = props => {
    const { onColumnResize } = this.props;
    const { onResize, width, onClick, ...restProps } = props;
    const [resizing, setResizing] = useState(false); // 是否拖拽结束的标志
    if (!width) {
      return <th {...restProps} />
    }
    return (
      <Resizable
       width={width} 
       height={0}
       draggableOpts={{ enableUserSelectHack: false }}
       onResizeStart={(e) => {
        /** #160 处理表格列头拖拽效果 start */
        this.table.className = `${this.table.className} ${FDX_ON_RESIZE_BODY}`;
        resizeHandle = e.target;
        resizeHandle.className = `${resizeHandle.className} ${RESIZ_HANDLER_ACTIVE_CLASS}`;
        if (this.table.querySelector(SCROLL_TABLE_CLASS)) { // 列头固定的滚动表格 需要特殊处理
          const scrollTableHeader = this.table.querySelector(SCROLL_TABLE_CLASS);
          const scrollBody = this.table.querySelector(TABLE_SCROLL_BODY);
          if (scrollBody) {
            scrollBody.className = `${scrollBody.className} ${FDX_TABLE_SCROLL_BODY}`
          }
          scrollTableClass = scrollTableHeader.className;
          scrollTableCss = scrollTableHeader.style.cssText;
          scrollTableHeader.style.cssText = '';
          scrollTableHeader.className = FDX_SCROLL_TABLE_CLASS;
        }
        resizeHandle.style.setProperty(TABLE_HEIGHT_CSS, `${this.table?.clientHeight}px`);
        /** #160 处理表格列头拖拽效果 end */
        setResizing(true)
       }}
       onResizeStop={() => {
        /** #160 处理表格列头拖拽效果 start */
        this.table.className = this.table.className.replace(` ${FDX_ON_RESIZE_BODY}`, '');
        resizeHandle.className = resizeHandle.className.replace(` ${RESIZ_HANDLER_ACTIVE_CLASS}`, '');
        resizeHandle.style.removeProperty(TABLE_HEIGHT_CSS);
        const scrollTableHeader = this.table.querySelector(`.${FDX_SCROLL_TABLE_CLASS}`);
        if (scrollTableHeader) {  // 列头固定的滚动表格 需要特殊处理
          scrollTableHeader.style.cssText = scrollTableCss;
          scrollTableHeader.className = scrollTableClass;
        }
        const scrollBody = this.table.querySelector(TABLE_SCROLL_BODY);
        if (scrollBody) {
          scrollBody.className = scrollBody.className.replace(` ${FDX_TABLE_SCROLL_BODY}`, '');
        }
        scrollTableClass = '';
        scrollTableCss = '';
        resizeHandle = null;
        /** #160 处理表格列头拖拽效果 end */
        setTimeout(() => { // setTimeout使得set-resizing-false (标志拖拽结束) 异步执行，即在拖拽结束的刹那不会触发点击事件
          setResizing(false);
        },0);
        if (onColumnResize) {
          const { columns } = this.state;
          const totalWidth = columns.reduce((acc, col) => {
            const width = typeof col.width === 'number' ? col.width : parseInt(col.width, 10) || 0;
            return acc + width;
          }, 0);
          // columns.forEach(col => {
          //   col.width = col.width ? col.width : Math.abs(this.table.offsetWidth - totalWidth)
          // });
          const curColumns = columns.map(col => {
            return {
              ...col,
              width: col.width ? col.width : Math.abs(this.table.offsetWidth - totalWidth)
            };
          });
          onColumnResize(curColumns);
        }
       }}
       onResize={onResize}>
        <th
       onClick={(...args) => {
          if (!resizing && onClick) {
            onClick(...args);
          }
        }}
        {...restProps} />
      </Resizable>
    );
  };

  handleSave = (row, type) => {
    const { rowKey, onSaveRowEdit, onSaveCellEdit, onCancelRowEdit } = this.props;
    const { dataSource } = this.state;
    const newData = [...dataSource];
    let tableKey = '';
    if (isFunction(rowKey)) {
      tableKey = findKeyByValue(rowKey(row), row)
    } else {
      tableKey = rowKey;
    }
    const index = newData.findIndex(item => lodashGet(row, tableKey) === lodashGet(item, tableKey));
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        ...row
      });
      this.setState({ dataSource: newData, update: true });
    } else {
      // 新增功能
      newData.push(row);
      this.setState({ dataSource: newData, update: true });
    }
    const { [regKey]: dataRegId } = this.props as any;

    if (type === "save") {
      onSaveRowEdit(row, newData);
      if (dataRegId)
        sendPagePoint({
          eventType: clickType,
          tagname: "table",
          value: row,
          [regKey]: dataRegId
        });
    } else if (type === "cancel") {
      onCancelRowEdit(row);
    } else if (type === "saveCell") {
      onSaveCellEdit(newData[index], index);
    }
  };

  moveRow = (dragIndex, hoverIndex) => {
    const { dataSource } = this.state;
    const { afterRowDrag } = this.props;
    const dragRow = dataSource[dragIndex];

    /* eslint-disable */
    const dragData = update(dataSource, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRow]
      ]
    });
    afterRowDrag(dragData);
    this.setState({ dataSource: dragData, update: true });
  };

  handleResize = index => (_e, { size }) => {
    this.setState(({ columns }) => {
      const nextColumns = [...columns];
      nextColumns[index] = {
        ...nextColumns[index],
        width: size.width
      };
      return { columns: nextColumns, update: true };
    });
  };

  isEditing = record => {
    const { rowKey } = this.props;
    const key = isFunction(rowKey) ? rowKey(record) : record[rowKey];
    const { editingKey } = this.state;
    return key === editingKey;
  };

  toggleEditRow = (key = "", record = {}, method) => {
    const { onRowEditing, saveAddRowMsg } = this.props;
    if (method === 'edit' && this.state.freezAddRow) {
      message.warn(saveAddRowMsg || '请先保存当前新增行！');
      return;
    }
    if (key) {
      onRowEditing(record);
    }
    this.setState({ editingKey: key, update: true, freezAddRow: false });
  };

  judgeCellEdit = columns => columns.some(item => item.editable === true);

  selectRow = record => {
    this.setState({ highlightRow: record, update: true });
  };

  handleSelectRow = (selectedRowKeys: string[], selectedRows: Array<any>, onChange: Function) => {
    const { onSelectRows } = this.props;
    if (onChange) {
      onChange(selectedRowKeys, selectedRows);
    }
    if (onSelectRows) {
      onSelectRows(selectedRowKeys);
    }
  }

  handleRowSelection = (selection) => {
    const { selectType } = this.props;
    const rowSelection = typeof selection === 'boolean' ? {} : (selection || {});
    if (selectType === 'radio') {
      rowSelection.type = selectType;
    }
    const newRowSelection = {
      ...rowSelection,
      onChange: (selectedRowKeys: string[], selectedRows: Array<any>) => this.handleSelectRow(selectedRowKeys, selectedRows, rowSelection.onChange),
    }
    if (!newRowSelection.onChange) {

    }
    return newRowSelection;
  }

  handleTableChange = (pagination, filters, sorter, extra) => {
    const { onChange, onSortChange } = this.props;
    const { currentSortInfo } = this.state;
    if (onChange) {
      onChange(pagination, filters, sorter, extra);
    }

    // 分页 筛选 排序都会触发onChange，需要区分排序
    if (currentSortInfo.field !== sorter.field || currentSortInfo.order !== sorter.order) {
      if (onSortChange) {
        onSortChange(sorter.field, sorter.order);
      }
      this.setState({ currentSortInfo: { field: sorter.field, order: sorter.order }});
    }
  }

  handlePageChange = (currentPage: number, pageSize: number) => {
    if (this.state.freezAddRow) {
      message.warn(this.props.saveAddRowMsg || '请先保存当前新增行！');
      return;
    }
    this.setState({
      currentPage,
    });
    if (this.props.pagination && this.props.pagination.onChange) {
      this.props.pagination.onChange(currentPage, pageSize);
    }
  }

  renderColTitle = (title: string | ReactNode | (({ sortOrder, sortColumn, filters }) => ReactNode)) => {
    const { columns, defaultColumns } = this.state;
    let titleComp = title;
    if (typeof title === 'function') {
      titleComp = ({ sortOrder, sortColumn, filters }) => (
        <div className="fdx-col-title">
          <span className="fdx-col-title-txt">{title({ sortOrder, sortColumn, filters })}</span>
          <span className="fdx-col-title-icon" onClick={(e) => e.stopPropagation()}>
            <Dropdown overlay={<ColModelMenu columns={columns} defaultColumns={defaultColumns} handleColModelMenu={this.handleColModelMenu} />} trigger={['click']}>
              <Icon type="ellipsis" />
            </Dropdown>
          </span>
        </div>
      )
    } else {
      titleComp = (
        <div className="fdx-col-title">
          <span className="fdx-col-title-txt">{title}</span>
          <span className="fdx-col-title-icon" onClick={(e) => e.stopPropagation()}>
            <Dropdown overlay={<ColModelMenu columns={columns} defaultColumns={defaultColumns} handleColModelMenu={this.handleColModelMenu} />} trigger={['click']}>
              <Icon type="ellipsis" />
            </Dropdown>
          </span>
        </div>
      )
    }
    return titleComp;
  }

  handleColModelMenu = (cols: any[]) => {
    const newCols = this.state.defaultColumns.filter(col => {
      if (col) {
        const colKey = col.dataIndex || col.key;
        if (cols.includes(colKey)) {
          const newCol = (find(this.state.columns, { dataIndex: colKey }) as IColumnProps) || (find(this.state.columns, { key: colKey }) as IColumnProps);
          col = cloneDeep(newCol);
          return true;
        }
        return false;
      }
      return false;
    });    
    this.setState({
      columns: newCols,
      // update: true
    });

    const { onColModelChange } = this.props;
    onColModelChange && onColModelChange(newCols);
  }

  render() {
    const { dragable, rowClassName, editRow, rowKey, onRow, rowSelection: propRowSelection, onSelectRows, onSortChange, autoHideSortIcon } = this.props;
    const { columns, dataSource, highlightRow } = this.state;
    const rowSelection = typeof propRowSelection === 'boolean' ? {} : {
      ...propRowSelection,
      columnWidth: propRowSelection?.columnWidth || 60
    };
    
    let editClass = "";
    if (editRow || this.judgeCellEdit(columns)) {
      editClass += "editable-row ";
    }
    this.components = {
      header: {
        cell: this.ResizeableTitle
      }
    };
    if (editRow) {
      this.components.body = {
        row: EditableFormRow,
        cell: EditableRow
      };
    } else if (this.judgeCellEdit(columns)) {
      this.components.body = {
        row: EditableFormCell,
        cell: EditableCell
      };
    } else if (dragable) {
      this.components.body = {
        row: DragableBodyRow
      };
    }

    const onRowNew= (record, index) => ({
      ...(dragable ? { moveRow: this.moveRow } : {}),
      ...(onRow ? onRow(record, index) : {}),
      index,
      onClick: event => {
        function findDomAncestor(dom) {
          if (dom.nodeName === "A" || dom.nodeName === "BUTTON" || dom.nodeName === "INPUT") {
            return true;
          } else if (dom.nodeName === "BODY") {
            return false;
          } else {
            return findDomAncestor(dom.parentNode);
          }
        }
        if (findDomAncestor(event.target)) {
          return;
        }
        if (onRow && onRow(record, index)["onClick"]) {
          onRow(record, index)["onClick"](event);
        }
        this.selectRow(record);
      }
    });

    const opts: any = {
      ...this.props,
      rowSelection,
      components: this.components,
      dataSource,
      columns: columns.map((col, index) => ({
        ...col,
        width: index === columns.length - 1 ? '' : col.width,
        title: !this.props.colModel ? col.title : this.renderColTitle(col.title),
        onHeaderCell: column => ({
          width: column.width,
          onResize: this.handleResize(index)
        }),
        className: `${col.className || ''} ${ this.props.autoHideFilterIcon ? 'hideFilterIcon': ''} ${ this.props.autoHideSortIcon ? (this.state.currentSortInfo.field === col.dataIndex ? 'activeSort' : 'hideSort' ) : ''}`,
        ...(col.editable
          ? {
              onCell: record => ({
                record,
                width: col.width,
                editType: col.editType || DEFAULT_COLUMN_EDITTYPE,
                dataIndex: col.dataIndex,
                editRules: col.editRules,
                editOptions: col.editOptions || [],
                optionsLabel: col.optionsLabel || DEFAULT_COLUMN_OPTIONS_LABEL,
                optionsValue: col.optionsValue || DEFAULT_COLUMN_OPTIONS_VALUE,
                editInitValue: col.editInitValue,
                relationKey: col.relationKey,
                title: col.title,
                editContent: isFunction(col.editContent)
                  ? col.editContent()
                  : "Edit",
                saveContent: isFunction(col.saveContent)
                  ? col.saveContent()
                  : "Save",
                cancelContent: isFunction(col.cancelContent)
                  ? col.cancelContent()
                  : "Cancel",
                ...(editRow
                  ? {
                      editing: this.isEditing(record),
                      handleSave: this.handleSave,
                      toggleEditRow: this.toggleEditRow,
                      keyValue: isFunction(rowKey)
                        ? rowKey(record)
                        : record[rowKey]
                    }
                  : { editable: col.editable, handleSave: this.handleSave })
              })
            }
          : {})
      })),
      rowClassName: (record, index) => {
        const res = [];
        if (rowClassName) {
          res.push(rowClassName(record, index));
        }
        if (editClass) {
          res.push(editClass);
        }
        if (isFunction(rowKey)) {
          if (rowKey(record) === rowKey(highlightRow)) {
            res.push("fdx-table-row-selected");
          }
        } else {
          if (record[rowKey] === highlightRow[rowKey]) {
            res.push("fdx-table-row-selected");
          }
        }
        return res.join(" ");
      },
      ref: ref => {
        this.table = findDOMNode(ref);
      }
    };
    // 若用户直接设置rowSelection=true，或rowSelection未定义onChange 则行选择在table内部处理并触发onSelectRows
    if (onSelectRows) {
      opts.rowSelection = this.handleRowSelection(rowSelection);
    }
    // 若用户设置onSortChange,需要在内部onChange事件并触发onSortChange
    if (onSortChange || autoHideSortIcon) {
      opts.onChange = this.handleTableChange
    }
    const { onChange, ...rest } = opts.pagination;
    return (
      <DndProvider backend={HTML5Backend}>
        <ConfigConsumer>
          {({ pagePoint }: ConfigConsumerEProps) => (
            <div className="fdx-table">
              {pagePoint ? (
                <PagePointEnhanced
                  compType="Table"
                  {...this.props}
                  onRow = { onRowNew }
                  render={eventprops => (
                    <AntdTable
                      {...opts}
                      rowSelection={
                        rowSelection ? { ...rowSelection,  ...(eventprops.onChange ? {onChange: eventprops.onChange}: {}) } : null
                      }
                      onRow = {eventprops.onRow ? eventprops.onRow : onRowNew}
                      pagination={opts.pagination ? {
                        current: this.state.currentPage,
                        onChange: this.handlePageChange,
                        ...rest
                      } : false}
                      data-ui-role='Table'
                    />
                  )}
                ></PagePointEnhanced>
              ) : (
                <AntdTable
                  {...opts} 
                  pagination={opts.pagination ? {
                    current: this.state.currentPage,
                    onChange: this.handlePageChange,
                    ...rest
                  } : false}
                  onRow = {onRowNew}
                />
              )}
            </div>
          )}
        </ConfigConsumer>
      </DndProvider>
    );
  }
}
export default Table;



// WEBPACK FOOTER //
// ./src/components/table/index.tsx